import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { appStore, doLogOut, isLoading$, isLoggedIn$, notifierStore } from "src/framework/store";
import { LocalStorage } from "../commons/LocalStorage";
import { SessionStorage } from "../commons";

export enum STATUS_CODE {
  NO_CONTENT = 204,
  OK = 200,
  CREATED = 201,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  INTERNAL_SERVER_ERROR = 500,
}

export interface IDataRequest {
  url: string;
  data?: object;
  headers?: object;
  withCredentials?: boolean;
  guarded?: boolean;
  onUploadProgress?: (progressEvent: any) => void
}

export interface IDataResponse<T = any> {
  data: T;
  statusCode: number;
  message: string;
}

export interface IHttpClient<T = any> {
  api: T;

  get(data: IDataRequest): Promise<IDataResponse<T>>;
  post(data: IDataRequest): Promise<IDataResponse<T>>;
  put(data: IDataRequest): Promise<IDataResponse<T>>;
  delete(data: IDataRequest): Promise<IDataResponse<T>>;

  getFile(data: IDataRequest): Promise<IDataResponse>;
}

class Api implements IHttpClient<AxiosInstance> {
  api: AxiosInstance;

  constructor(baseUrl: string, readonly noLoading: boolean = false) {
    this.api = axios.create({
      baseURL: baseUrl,
      withCredentials: false,
    });

    this.api.interceptors.request.use((request) => {
      if (!this.noLoading) {
        isLoading$.next(true);
      }

      if (isLoggedIn$.getValue()) {

        request.headers = {
          ...request.headers,
          ...{ Authorization: `Bearer ${LocalStorage.get("solar.token")}` },
        };
      }
      return request;
    });
    this.api.interceptors.response.use(
      (response) => {
        if (response.headers.newtoken) {
          LocalStorage.set("solar.token", response.headers.newtoken)
        }
        isLoading$.next(false);
        return response;
      },
      (error: AxiosError) => {
        isLoading$.next(false);
        if (error?.response?.status === STATUS_CODE.UNAUTHORIZED) {
          notifierStore.setNotifier({
            timeOut: 3500,
            title: "Aviso!",
            content: "Seu acesso ao Portal de Pagamentos expirou. Efetue o login novamente.",
            onClose: async () => {
              const redirectURL = await doLogOut();
              notifierStore.setOpen(false);
              if (redirectURL) {
                window.location.href = redirectURL;
                LocalStorage.removeAll();
                SessionStorage.removeAll();
                isLoggedIn$.next(false);
              }
            },
          })
          notifierStore.setOpen(true)
        } else if (error?.response?.data.message) {
          appStore.setError([...error?.response?.data.message]);
        } /* else {
          appStore.setError([error]);
        } */
      }
    );
  }

  async getFile(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse = await this.api.get(data.url, {
        headers: data.headers,
        responseType: "arraybuffer",
      });

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nenhum arquivo retornado",
          statusCode: _resp.status,
        };
      }

      return {
        data: _resp.data,
        message: "Ok",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }

  async get(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse = await this.api.get(data.url, {
        headers: data.headers,
      });
      if (_resp === undefined) {
        return {
          data: [],
          message: "Api está indisponível",
          statusCode: 500,
        };
      }

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nunhum conteúdo a ser exibido",
          statusCode: _resp.status,
        };
      }

      return {
        data: _resp.data.data,
        message: _resp.data.message ? _resp.data.message : "Ok",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }

  async post(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse = await this.api.post(data.url, data.data, {
        headers: data.headers || {},
        onUploadProgress: data?.onUploadProgress
      });

      if (!_resp) {
        return {
          data: [],
          message: appStore.getError(),
          statusCode: 400,
        };
      }

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nunhum conteúdo a ser exibido",
          statusCode: _resp.status,
        };
      }

      return {
        data: _resp.data.data,
        message: _resp?.data?.message || "OK",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }

  async put(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse = await this.api.put(data.url, data.data, {
        headers: data.headers || {},
      });

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nenhum conteúdo a ser exibido",
          statusCode: _resp.status,
        };
      }

      return {
        data: _resp.data.data,
        message: _resp?.data?.message || "OK",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }

  async delete(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse = await this.api.delete(data.url, data.data);

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nunhum conteúdo a ser exibido",
          statusCode: _resp.status,
        };
      }

      return {
        data: _resp.data.data,
        message: "Ok",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }
}

const api = new Api(`${process.env.REACT_APP_API_BASE_URL}`);
const apiNoLoading = new Api(`${process.env.REACT_APP_API_BASE_URL}`, true);


export default api;
export { apiNoLoading };
